import { createColumnHelper } from '@tanstack/react-table'
import { INote } from 'models/contact_companies'
import { useAuth } from '../../../context/authentication/useAuth'
import { API_HOST } from 'utils/http-client'
import { AFbLink } from 'components/FbUI/FbLink/styles'
import { Editor } from 'components/Editor/Editor'
import { JsonIntoUl } from 'components/json-into-ul'
import { ActionButtons } from '../../Buttons/ActionButtons'
import { BiExpandAlt } from 'react-icons/bi'
import { NOTES_COLUMNS } from './notesColumnInfo'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { TaglistCell } from 'components/FbUI/StagePill'
import {
  createDeleteAction,
  createEditAction,
} from 'components/Buttons/ActionsDropdown'
import { PinnableRowActions } from 'components/Buttons/PinnableRowActions'
import { PiEye } from 'react-icons/pi'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { InitialsAvatar } from 'components/Tables/CommonTableCells/InitialsAvatar'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'

interface UseNotesTableColumnsProps {
  setActiveNote: (noteData: { note: INote; isViewing?: boolean } | null) => void
  handleNoteDelete: (noteId: number) => Promise<void>
  showDoorColumns?: boolean
}

export const useNotesTableColumns = ({
  setActiveNote,
  handleNoteDelete,
  showDoorColumns = false,
}: UseNotesTableColumnsProps) => {
  const { user: me } = useAuth()
  const { preferences } = usePreferences()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}
  const linkVariant = useTableLinkVariant()

  const columnHelper = createColumnHelper<INote>()
  const notesColumnHelper = createTableColumnHelper({
    columns: NOTES_COLUMNS,
    tooltips,
  })

  const baseColumns = [
    columnHelper.accessor('user', {
      ...notesColumnHelper.retrieveColumn('user'),
      cell: (info) => {
        const row = info.row.original
        const name = row.user
          ? `${row.user.first_name ?? ''} ${row.user.last_name ?? ''}`.trim()
          : `${me?.company.name ?? ''} User`
        const initials = row.user
          ? `${row.user.first_name?.[0] ?? ''}${row.user.last_name?.[0] ?? ''}`
              .toUpperCase()
              .slice(0, 2)
          : (me?.company.name?.[0] ?? 'U').toUpperCase()
        return (
          <div className="flex items-center gap-2.5">
            <InitialsAvatar initials={initials} />
            <span className="truncate">{name}</span>
          </div>
        )
      },
      size: 220,
    }),
    columnHelper.accessor('content', {
      ...notesColumnHelper.retrieveColumn('content'),
      size: 340,
      cell: (info) => {
        const value = info.getValue()
        if (!value) return '--'

        const preview = (
          <div className="line-clamp-2 w-full min-w-0 max-w-full break-words">
            <JsonIntoUl
              value={value}
              fallback={
                <Editor
                  value={value}
                  readOnly={true}
                  disableToolbar={true}
                  borderless={true}
                />
              }
            />
          </div>
        )

        if (linkVariant === 'plain') {
          return (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-full min-w-0 max-w-full cursor-pointer text-left hover:text-neutral-900 [&_*]:!cursor-pointer"
                >
                  {preview}
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="bottom"
                align="center"
                sideOffset={-32}
                className="max-h-80 w-96 overflow-y-auto !p-3"
              >
                <div className="text-sm text-neutral-700">
                  <JsonIntoUl
                    value={value}
                    fallback={
                      <Editor
                        value={value}
                        readOnly={true}
                        disableToolbar={true}
                        borderless={true}
                      />
                    }
                  />
                </div>
              </PopoverContent>
            </Popover>
          )
        }

        return preview
      },
    }),
    columnHelper.accessor('attachments', {
      ...notesColumnHelper.retrieveColumn('has_attachment'),
      cell: (info) => {
        const attachments = info.getValue()
        if (!attachments || attachments.length === 0) return '--'
        return (
          <div className="flex flex-col">
            {attachments?.map((attachment) => {
              const href = attachment?.file?.startsWith('/')
                ? `${API_HOST}${attachment.file}`
                : attachment.file
              return (
                <AFbLink
                  key={attachment.id}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {attachment.original_filename}
                </AFbLink>
              )
            })}
          </div>
        )
      },
      size: 150,
    }),
    columnHelper.accessor('created', {
      ...notesColumnHelper.retrieveColumn('created'),
      cell: (info) => {
        const date = info.getValue()
        if (!date) return '-'
        return new Date(date).toLocaleDateString()
      },
      size: 100,
    }),
    columnHelper.accessor('modified', {
      ...notesColumnHelper.retrieveColumn('modified'),
      cell: (info) => {
        const date = info.getValue()
        if (!date) return '-'
        return new Date(date).toLocaleDateString()
      },
      size: 100,
    }),
  ]

  const doorColumns = showDoorColumns
    ? [
        columnHelper.accessor('door_proxy', {
          ...notesColumnHelper.retrieveColumn('door_country'),
          id: 'door_country',
          cell: (info) => {
            const doorProxy = info.getValue()
            if (!doorProxy) return '--'
            return doorProxy.country || '--'
          },
          size: 120,
        }),
        columnHelper.accessor('door_proxy', {
          ...notesColumnHelper.retrieveColumn('door_city'),
          id: 'door_city',
          cell: (info) => {
            const doorProxy = info.getValue()
            if (!doorProxy) return '--'
            return doorProxy.city || '--'
          },
          size: 120,
        }),
        columnHelper.accessor('door_proxy', {
          ...notesColumnHelper.retrieveColumn('door_state'),
          id: 'door_state',
          cell: (info) => {
            const doorProxy = info.getValue()
            if (!doorProxy) return '--'
            return doorProxy.state || '--'
          },
          size: 100,
        }),
        columnHelper.display({
          ...notesColumnHelper.retrieveColumn('door_address'),
          id: 'door_address',
          cell: (info) => {
            const doorProxy = info.row.original.door_proxy
            if (!doorProxy) return '--'
            return doorProxy.address || '--'
          },
          size: 200,
        }),
        columnHelper.accessor('door_proxy', {
          ...notesColumnHelper.retrieveColumn('door_tags'),
          id: 'door_tags',
          cell: (info) => {
            const doorProxy = info.getValue()
            if (
              !doorProxy ||
              !doorProxy.taglist ||
              doorProxy.taglist.length === 0
            )
              return '--'

            return <TaglistCell taglist={doorProxy.taglist} />
          },
          size: 200,
        }),
      ]
    : []

  const actionColumn = columnHelper.display({
    ...notesColumnHelper.retrieveColumn('actions'),
    header:
      linkVariant === 'plain'
        ? () => <div className="w-full text-center">Actions</div>
        : 'Actions',
    meta: {
      isEditable: false,
      isLastColumn: true,
      rightAlign: linkVariant !== 'plain',
    },
    cell: (info) => {
      const note = info.row.original
      const noteId = note.id

      if (linkVariant === 'plain') {
        return (
          <PinnableRowActions
            target={{ targetType: 'note', targetId: noteId }}
            pinLabel={(note.content ?? '').slice(0, 40) || 'note'}
            className="justify-center"
            actionsBefore={[
              {
                label: 'View',
                icon: PiEye,
                onClick: () => setActiveNote({ note, isViewing: true }),
              },
              createEditAction(() => setActiveNote({ note })),
            ]}
            actionsAfter={[createDeleteAction(() => handleNoteDelete(noteId))]}
          />
        )
      }

      return (
        <ActionButtons
          className={'justify-start'}
          onDelete={() => handleNoteDelete(noteId)}
          onEdit={() => setActiveNote({ note })}
          onSee={() => setActiveNote({ note, isViewing: true })}
          seeIcon={BiExpandAlt}
        />
      )
    },
    size: linkVariant === 'plain' ? 70 : 120,
  })

  return [...baseColumns, ...doorColumns, actionColumn]
}
